import { useEffect, useRef, useState } from 'react';
import { devices } from '../devices/devices';
import { DeviceStatusFlags } from '../devices/MonitoredDevice';
import logger from '../helpers/logger';

export enum DeviceType {
	CashAcceptor = 'CashAcceptor',
	CheckReader = 'CheckReader',
}

export interface DeviceAvailability {
	[DeviceType.CashAcceptor]: boolean;
	[DeviceType.CheckReader]: boolean;
}

let isChecking = false;

interface UseDeviceAvailabilityOptions {
	enabled?: boolean;
}

/**
 * Custom hook to monitor the availability of kiosk devices (Cash Acceptor and Check Reader).
 *
 * This hook checks the status of the supported devices and updates their availability state.
 * If a device transitions from available to unavailable
 *
 * @param options - Configuration options for the hook.
 * @param {boolean} [options.enabled=true] - Whether the availability check is enabled.
 * @returns {DeviceAvailability} An object representing the current availability status of each device type.
 *
 * @example
 * // Check status for a single device (e.g., Cash Acceptor)
 * const { [DeviceType.CashAcceptor]: isCashAcceptorAvailable } = useDeviceAvailability();
 *
 * if (!isCashAcceptorAvailable) {
 *   console.log('Cash Acceptor is unavailable');
 * }
 */
export default function useDeviceAvailability({ enabled = true }: UseDeviceAvailabilityOptions = {}) {
	const [availability, setAvailability] = useState<DeviceAvailability>({
		[DeviceType.CashAcceptor]: true,
		[DeviceType.CheckReader]: true,
	});

	const previousAvailabilityRef = useRef<DeviceAvailability>(availability);

	const handleDeviceUnavailable = async (deviceType: DeviceType) => {
		switch (deviceType) {
			case DeviceType.CashAcceptor:
				logger.logCashAcceptor.error({}, 'Cash Acceptor became unavailable', 'Cash Acceptor is not responding');
				break;
			case DeviceType.CheckReader:
				logger.logCheckScanner.error({}, 'Check Reader became unavailable', 'Check Reader is not responding');
				break;
			default:
				logger.logGeneral.error({}, 'Unknown device became unavailable', `Device type: ${deviceType}`);
		}
	};

	useEffect(() => {
		if (!enabled) return;

		const checkDeviceStatus = (deviceType: DeviceType): boolean => {
			try {
				let status;
				switch (deviceType) {
					case DeviceType.CashAcceptor:
						status = devices.currencyDevice.getStatus();
						break;
					case DeviceType.CheckReader:
						status = devices.checkReader.getStatus();
						break;
					default:
						return true;
				}

				return status.statusBits === DeviceStatusFlags.Ok;
			} catch (error) {
				logger.logGeneral.error({}, `Error checking ${deviceType} status:`, error);
				return false;
			}
		};

		const checkAllDevices = async () => {
			if (isChecking) return;
			isChecking = true;

			try {
				const newAvailability: DeviceAvailability = {
					[DeviceType.CashAcceptor]: checkDeviceStatus(DeviceType.CashAcceptor),
					[DeviceType.CheckReader]: checkDeviceStatus(DeviceType.CheckReader),
				};

				setAvailability(newAvailability);

				// Check if any device became unavailable and trigger action
				Object.entries(newAvailability).forEach(async ([deviceType, isAvailable]) => {
					const wasAvailable = previousAvailabilityRef.current[deviceType as DeviceType];
					if (wasAvailable && !isAvailable) {
						logger.logGeneral.warn({}, `Device ${deviceType} became unavailable`);
						await handleDeviceUnavailable(deviceType as DeviceType);
					}
				});

				previousAvailabilityRef.current = newAvailability;
			} catch (error) {
				logger.logGeneral.error({}, 'Error checking device availability:', error);
			} finally {
				isChecking = false;
			}
		};

		checkAllDevices();
	}, [enabled]);

	return availability;
}
